import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import './mobile-menu-enhanced.js';
import './animations-disabled.js';
import './clean-menu.js';
import './card-marquee.js';
import './scroll-animations.js';
import './toast-notifications.js';
import './keyboard-shortcuts.js';
import './navigation-animations.js';

// Configure Swiper to use modules
Swiper.use([Navigation, Pagination, Autoplay, EffectFade]);

/**
 * PCR Card Modern JavaScript Interactions
 * Professional, high-class interactions for the redesigned site
 */

class PCRCardInteractions {
    constructor() {
        this.initializeOnLoad();
    }

    initializeOnLoad() {
        // Hide loader when page is fully loaded
        window.addEventListener('load', () => {
            const loader = document.querySelector('.page-loader');
            if (loader) {
                loader.classList.add('fade-out');
                setTimeout(() => {
                    loader.style.display = 'none';
                }, 500);
            }
        });

        document.addEventListener('DOMContentLoaded', () => {
            // Navigation animations now handled by navigation-animations.js
            // this.initHeaderScroll(); // Disabled - using advanced navigation animations
            this.initMobileMenu();
            this.initScrollToTop();
            this.initHeroCarousel();
            // this.initCounterAnimations(); // Disabled - showing static values instead
            this.initIntersectionObservers();
            this.initSmoothScrolling();
            this.initFeatureCards();
            this.initTestimonialCarousel();
            this.initFormValidation();
            this.initPageTransitions();
            this.initSkeletonLoaders();
            this.initLazyLoading();
            this.initKeyboardNavigation();
            this.initTooltips();
            this.initSearchAutocomplete();
            this.initDarkMode();
            this.initPerformanceMonitoring();
        });
    }

    /**
     * Header Scroll Animation
     * Compacts the header when scrolling down
     */
    initHeaderScroll() {
        const header = document.querySelector('.site-header');
        if (!header) return;

        let lastScrollTop = 0;
        let ticking = false;

        const updateHeader = () => {
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            
            if (scrollTop > 50) {
                header.classList.add('scrolled');
            } else {
                header.classList.remove('scrolled');
            }

            // Add hide/show on scroll direction
            if (scrollTop > lastScrollTop && scrollTop > 100) {
                // Scrolling down
                header.style.transform = 'translateY(-100%)';
            } else {
                // Scrolling up
                header.style.transform = 'translateY(0)';
            }

            lastScrollTop = scrollTop;
            ticking = false;
        };

        const requestTick = () => {
            if (!ticking) {
                window.requestAnimationFrame(updateHeader);
                ticking = true;
            }
        };

        window.addEventListener('scroll', requestTick);
    }

    /**
     * Hero Carousel with Loading Indicator
     * Creates a professional carousel with progress bar
     */
    initHeroCarousel() {
        const heroContainer = document.querySelector('.hero-carousel-container');
        if (!heroContainer) return;

        // Initialize loading bar
        const loadingProgress = document.querySelector('.carousel-loading-progress');
        
        // Create Swiper instance
        const heroSwiper = new Swiper('.hero-swiper', {
            modules: [Navigation, Pagination, Autoplay, EffectFade],
            effect: 'fade',
            fadeEffect: {
                crossFade: true
            },
            speed: 1000,
            autoplay: {
                delay: 5000,
                disableOnInteraction: false,
            },
            loop: true,
            pagination: {
                el: '.swiper-pagination',
                clickable: true,
                renderBullet: (index, className) => {
                    return `<span class="${className}">
                        <svg class="progress-ring" width="24" height="24">
                            <circle class="progress-ring__circle" stroke="white" stroke-width="2" fill="transparent" r="10" cx="12" cy="12"/>
                        </svg>
                    </span>`;
                },
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            on: {
                init: () => {
                    this.startLoadingBar(loadingProgress, 5000);
                },
                slideChange: () => {
                    this.resetLoadingBar(loadingProgress);
                    this.startLoadingBar(loadingProgress, 5000);
                },
                autoplayStop: () => {
                    this.resetLoadingBar(loadingProgress);
                },
            },
        });
    }

    startLoadingBar(element, duration) {
        if (!element) return;
        
        element.classList.remove('active');
        void element.offsetWidth; // Trigger reflow
        element.classList.add('active');
        element.style.transition = `width ${duration}ms linear`;
    }

    resetLoadingBar(element) {
        if (!element) return;
        
        element.classList.remove('active');
        element.style.transition = 'none';
        element.style.width = '0';
    }

    /**
     * Counter Animations
     * Animates numbers counting up when visible
     */
    initCounterAnimations() {
        const counters = document.querySelectorAll('.stat-number');
        
        counters.forEach(counter => {
            counter.setAttribute('data-target', counter.innerText);
            counter.innerText = '0';
        });

        const animateCounter = (counter) => {
            const target = +counter.getAttribute('data-target');
            const duration = 2000; // 2 seconds
            const increment = target / (duration / 16); // 60fps
            let current = 0;

            const updateCounter = () => {
                current += increment;
                if (current < target) {
                    counter.innerText = Math.ceil(current).toLocaleString();
                    requestAnimationFrame(updateCounter);
                } else {
                    counter.innerText = target.toLocaleString();
                }
            };

            updateCounter();
        };

        // Use Intersection Observer to trigger animation
        const counterObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !entry.target.classList.contains('animated')) {
                        animateCounter(entry.target);
                        entry.target.classList.add('animated');
                    }
                });
            },
            { threshold: 0.5 }
        );

        counters.forEach(counter => counterObserver.observe(counter));
    }

    /**
     * Intersection Observer for Scroll Animations
     * Adds animations when elements come into view
     */
    initIntersectionObservers() {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible');
                    
                    // Add staggered animation for children
                    const children = entry.target.querySelectorAll('.animate-child');
                    children.forEach((child, index) => {
                        setTimeout(() => {
                            child.classList.add('visible');
                        }, index * 100);
                    });
                }
            });
        }, observerOptions);

        // Observe all animatable elements
        document.querySelectorAll('.fade-in, .slide-in-left, .slide-in-right, .feature-card, .process-step').forEach(el => {
            observer.observe(el);
        });
    }

    /**
     * Smooth Scrolling for Anchor Links
     */
    initSmoothScrolling() {
        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', function (e) {
                const href = this.getAttribute('href');
                
                // Skip if it's just "#" or empty
                if (!href || href === '#') return;
                
                e.preventDefault();
                const target = document.querySelector(href);
                
                if (target) {
                    const header = document.querySelector('.site-header, .modern-header');
                    const headerHeight = header ? header.offsetHeight : 80;
                    const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 20;
                    
                    window.scrollTo({
                        top: targetPosition,
                        behavior: 'smooth'
                    });
                }
            });
        });
        
        // Also add smooth scroll CSS as fallback
        document.documentElement.style.scrollBehavior = 'smooth';
    }

    /**
     * Feature Cards Hover Effects
     * Adds advanced hover interactions
     */
    initFeatureCards() {
        const cards = document.querySelectorAll('.feature-card');
        
        cards.forEach(card => {
            card.addEventListener('mouseenter', (e) => {
                const rect = card.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                
                card.style.setProperty('--mouse-x', `${x}px`);
                card.style.setProperty('--mouse-y', `${y}px`);
            });

            card.addEventListener('mousemove', (e) => {
                const rect = card.getBoundingClientRect();
                const x = e.clientX - rect.left;
                const y = e.clientY - rect.top;
                
                card.style.setProperty('--mouse-x', `${x}px`);
                card.style.setProperty('--mouse-y', `${y}px`);
            });
        });
    }

    /**
     * Testimonial Carousel
     */
    initTestimonialCarousel() {
        const testimonialSwiper = document.querySelector('.testimonial-swiper');
        if (!testimonialSwiper) return;

        new Swiper('.testimonial-swiper', {
            modules: [Navigation, Pagination, Autoplay],
            slidesPerView: 1,
            spaceBetween: 30,
            autoplay: {
                delay: 6000,
                disableOnInteraction: false,
            },
            pagination: {
                el: '.swiper-pagination',
                clickable: true,
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
            breakpoints: {
                768: {
                    slidesPerView: 2,
                },
                1024: {
                    slidesPerView: 3,
                },
            },
        });
    }

    /**
     * Mobile Menu Toggle
     * Handles hamburger menu animation and navigation display
     */
    initMobileMenu() {
        const menuToggle = document.querySelector('.mobile-menu-toggle');
        const mobileMenu = document.querySelector('.mobile-menu');
        const mobileOverlay = document.querySelector('.mobile-menu-overlay');
        
        if (!menuToggle || !mobileMenu) return;
        
        menuToggle.addEventListener('click', () => {
            const isActive = menuToggle.classList.contains('active');
            
            if (isActive) {
                // Close menu
                menuToggle.classList.remove('active');
                mobileMenu.classList.remove('active');
                if (mobileOverlay) mobileOverlay.classList.remove('active');
                document.body.style.overflow = '';
            } else {
                // Open menu
                menuToggle.classList.add('active');
                mobileMenu.classList.add('active');
                if (mobileOverlay) mobileOverlay.classList.add('active');
                document.body.style.overflow = 'hidden';
            }
        });

        // Close menu when clicking overlay
        if (mobileOverlay) {
            mobileOverlay.addEventListener('click', () => {
                menuToggle.classList.remove('active');
                mobileMenu.classList.remove('active');
                mobileOverlay.classList.remove('active');
                document.body.style.overflow = '';
            });
        }

        // Close menu when clicking on a link
        mobileMenu.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', () => {
                menuToggle.classList.remove('active');
                mobileMenu.classList.remove('active');
                if (mobileOverlay) mobileOverlay.classList.remove('active');
                document.body.style.overflow = '';
            });
        });
    }

    /**
     * Scroll to Top Button
     * Shows/hides based on scroll position
     */
    initScrollToTop() {
        const scrollBtn = document.querySelector('.scroll-to-top');
        if (!scrollBtn) return;

        // Show/hide button based on scroll position
        window.addEventListener('scroll', () => {
            if (window.pageYOffset > 300) {
                scrollBtn.classList.add('visible');
            } else {
                scrollBtn.classList.remove('visible');
            }
        });

        // Smooth scroll to top
        scrollBtn.addEventListener('click', () => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        });
    }

    /**
     * Page Transitions
     * Smooth transitions between pages
     */
    initPageTransitions() {
        // Add fade-in class to body on page load
        document.body.classList.add('page-transition-in');

        // Handle all internal links
        const links = document.querySelectorAll('a[href^="/"], a[href^="' + window.location.origin + '"]');
        
        links.forEach(link => {
            // Skip links with target="_blank" or download attribute
            if (link.target === '_blank' || link.hasAttribute('download')) return;
            
            // Skip hash links on the same page
            if (link.href.includes('#') && link.href.split('#')[0] === window.location.href.split('#')[0]) return;

            link.addEventListener('click', (e) => {
                // Allow modifier keys for new tab/window
                if (e.ctrlKey || e.shiftKey || e.metaKey || e.button === 1) return;

                e.preventDefault();
                const href = link.href;

                // Add fade-out class
                document.body.classList.add('page-transition-out');

                // Navigate after animation
                setTimeout(() => {
                    window.location.href = href;
                }, 300);
            });
        });
    }

    /**
     * Skeleton Loaders
     * Replace skeleton screens with actual content
     */
    initSkeletonLoaders() {
        const skeletons = document.querySelectorAll('.skeleton-loader');
        
        skeletons.forEach(skeleton => {
            // Simulate content loading (in real app, this would be after data fetch)
            const content = skeleton.nextElementSibling;
            
            if (content && content.classList.contains('skeleton-content')) {
                // Hide skeleton and show content
                setTimeout(() => {
                    skeleton.style.display = 'none';
                    content.style.display = 'block';
                    content.classList.add('fade-in');
                }, 1000); // Adjust timing based on actual data loading
            }
        });

        // For dynamic content loading
        this.observeSkeletonLoaders();
    }

    /**
     * Observe Skeleton Loaders for Dynamic Content
     */
    observeSkeletonLoaders() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === 1 && node.classList && node.classList.contains('skeleton-loader')) {
                            // Process newly added skeleton loaders
                            setTimeout(() => {
                                node.style.display = 'none';
                                const content = node.nextElementSibling;
                                if (content && content.classList.contains('skeleton-content')) {
                                    content.style.display = 'block';
                                    content.classList.add('fade-in');
                                }
                            }, 1000);
                        }
                    });
                }
            });
        });

        // Start observing the document body for changes
        // Check if document.body exists before observing it
        if (document.body) {
            observer.observe(document.body, {
                childList: true,
                subtree: true
            });
        } else {
            // If body doesn't exist yet, wait for DOM to be ready
            document.addEventListener('DOMContentLoaded', () => {
                if (document.body) {
                    observer.observe(document.body, {
                        childList: true,
                        subtree: true
                    });
                }
            });
        }
    }

    /**
     * Form Validation and Enhancement
     */
    initFormValidation() {
        const forms = document.querySelectorAll('.needs-validation');
        
        forms.forEach(form => {
            form.addEventListener('submit', (event) => {
                if (!form.checkValidity()) {
                    event.preventDefault();
                    event.stopPropagation();
                }
                
                form.classList.add('was-validated');
            }, false);

            // Real-time validation
            const inputs = form.querySelectorAll('input, textarea, select');
            inputs.forEach(input => {
                input.addEventListener('blur', () => {
                    if (input.checkValidity()) {
                        input.classList.remove('is-invalid');
                        input.classList.add('is-valid');
                    } else {
                        input.classList.remove('is-valid');
                        input.classList.add('is-invalid');
                    }
                });
            });
        });
    }

    /**
     * Lazy Loading for Images
     * Loads images only when they're about to enter viewport
     */
    initLazyLoading() {
        const images = document.querySelectorAll('img[data-src]');
        
        if ('IntersectionObserver' in window) {
            const imageObserver = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const img = entry.target;
                        const src = img.getAttribute('data-src');
                        
                        // Create a new image to preload
                        const tempImg = new Image();
                        tempImg.onload = () => {
                            img.src = src;
                            img.classList.add('loaded');
                            img.removeAttribute('data-src');
                        };
                        tempImg.src = src;
                        
                        observer.unobserve(img);
                    }
                });
            }, {
                rootMargin: '50px 0px',
                threshold: 0.01
            });

            images.forEach(img => imageObserver.observe(img));
        } else {
            // Fallback for browsers without IntersectionObserver
            images.forEach(img => {
                img.src = img.getAttribute('data-src');
                img.removeAttribute('data-src');
            });
        }
    }

    /**
     * Keyboard Navigation Support
     * Enhances accessibility with keyboard shortcuts
     */
    initKeyboardNavigation() {
        // Focus trap for modals
        const trapFocus = (element) => {
            const focusableElements = element.querySelectorAll(
                'a[href], button, textarea, input[type="text"], input[type="radio"], input[type="checkbox"], select'
            );
            const firstFocusable = focusableElements[0];
            const lastFocusable = focusableElements[focusableElements.length - 1];

            element.addEventListener('keydown', (e) => {
                if (e.key === 'Tab') {
                    if (e.shiftKey) {
                        if (document.activeElement === firstFocusable) {
                            lastFocusable.focus();
                            e.preventDefault();
                        }
                    } else {
                        if (document.activeElement === lastFocusable) {
                            firstFocusable.focus();
                            e.preventDefault();
                        }
                    }
                }
            });
        };

        // Global keyboard shortcuts
        document.addEventListener('keydown', (e) => {
            // Alt + H: Go to home
            if (e.altKey && e.key === 'h') {
                e.preventDefault();
                window.location.href = '/';
            }
            
            // Alt + S: Focus search
            if (e.altKey && e.key === 's') {
                e.preventDefault();
                const searchInput = document.querySelector('.search-input, input[type="search"]');
                if (searchInput) searchInput.focus();
            }
            
            // Escape: Close mobile menu
            if (e.key === 'Escape') {
                const menuToggle = document.querySelector('.mobile-menu-toggle');
                const nav = document.querySelector('.modern-nav');
                if (menuToggle && nav) {
                    menuToggle.classList.remove('active');
                    nav.classList.remove('mobile-active');
                    document.body.classList.remove('mobile-menu-open');
                }
            }
            
            // Alt + D: Toggle dark mode
            if (e.altKey && e.key === 'd') {
                e.preventDefault();
                this.toggleDarkMode();
            }
        });

        // Add skip to content link
        const skipLink = document.createElement('a');
        skipLink.href = '#main-content';
        skipLink.className = 'skip-to-content';
        skipLink.textContent = 'Skip to main content';
        document.body.insertBefore(skipLink, document.body.firstChild);
    }

    /**
     * Animated Tooltips and Popovers
     * Creates smooth tooltip animations
     */
    initTooltips() {
        const tooltipElements = document.querySelectorAll('[data-tooltip]');
        
        tooltipElements.forEach(element => {
            let tooltip = null;
            
            element.addEventListener('mouseenter', (e) => {
                const text = element.getAttribute('data-tooltip');
                const position = element.getAttribute('data-tooltip-position') || 'top';
                
                // Create tooltip element
                tooltip = document.createElement('div');
                tooltip.className = `custom-tooltip tooltip-${position}`;
                tooltip.textContent = text;
                document.body.appendChild(tooltip);
                
                // Position tooltip
                const rect = element.getBoundingClientRect();
                const tooltipRect = tooltip.getBoundingClientRect();
                
                let top, left;
                
                switch(position) {
                    case 'top':
                        top = rect.top - tooltipRect.height - 10;
                        left = rect.left + (rect.width - tooltipRect.width) / 2;
                        break;
                    case 'bottom':
                        top = rect.bottom + 10;
                        left = rect.left + (rect.width - tooltipRect.width) / 2;
                        break;
                    case 'left':
                        top = rect.top + (rect.height - tooltipRect.height) / 2;
                        left = rect.left - tooltipRect.width - 10;
                        break;
                    case 'right':
                        top = rect.top + (rect.height - tooltipRect.height) / 2;
                        left = rect.right + 10;
                        break;
                }
                
                tooltip.style.top = `${top + window.scrollY}px`;
                tooltip.style.left = `${left + window.scrollX}px`;
                
                // Animate in
                setTimeout(() => tooltip.classList.add('visible'), 10);
            });
            
            element.addEventListener('mouseleave', () => {
                if (tooltip) {
                    tooltip.classList.remove('visible');
                    setTimeout(() => {
                        if (tooltip && tooltip.parentNode) {
                            tooltip.parentNode.removeChild(tooltip);
                        }
                    }, 300);
                }
            });
        });
    }

    /**
     * Search Autocomplete
     * Adds intelligent search suggestions
     */
    initSearchAutocomplete() {
        const searchInputs = document.querySelectorAll('.search-input, input[type="search"]');
        
        searchInputs.forEach(input => {
            let autocompleteList = null;
            let currentFocus = -1;
            
            // Sample suggestions (in real app, fetch from API)
            const suggestions = [
                'Charizard Base Set',
                'Pikachu Promo',
                'Mewtwo EX',
                'Rayquaza VMAX',
                'Umbreon Gold Star',
                'Lugia Crystal',
                'Ancient Mew',
                'Birthday Pikachu',
                'First Edition Cards',
                'Japanese Exclusives'
            ];
            
            input.addEventListener('input', function() {
                const value = this.value;
                closeAllLists();
                
                if (!value) return;
                
                currentFocus = -1;
                
                // Create autocomplete list
                autocompleteList = document.createElement('div');
                autocompleteList.className = 'autocomplete-items';
                this.parentNode.appendChild(autocompleteList);
                
                // Filter and display suggestions
                suggestions.forEach(suggestion => {
                    if (suggestion.toLowerCase().includes(value.toLowerCase())) {
                        const item = document.createElement('div');
                        item.className = 'autocomplete-item';
                        
                        // Highlight matching text
                        const regex = new RegExp(`(${value})`, 'gi');
                        item.innerHTML = suggestion.replace(regex, '<strong>$1</strong>');
                        
                        item.addEventListener('click', function() {
                            input.value = suggestion;
                            closeAllLists();
                            input.dispatchEvent(new Event('change'));
                        });
                        
                        autocompleteList.appendChild(item);
                    }
                });
            });
            
            // Keyboard navigation
            input.addEventListener('keydown', function(e) {
                let items = autocompleteList ? autocompleteList.getElementsByClassName('autocomplete-item') : [];
                
                if (e.key === 'ArrowDown') {
                    currentFocus++;
                    addActive(items);
                } else if (e.key === 'ArrowUp') {
                    currentFocus--;
                    addActive(items);
                } else if (e.key === 'Enter') {
                    e.preventDefault();
                    if (currentFocus > -1 && items[currentFocus]) {
                        items[currentFocus].click();
                    }
                }
            });
            
            function addActive(items) {
                if (!items) return;
                removeActive(items);
                
                if (currentFocus >= items.length) currentFocus = 0;
                if (currentFocus < 0) currentFocus = items.length - 1;
                
                items[currentFocus].classList.add('autocomplete-active');
            }
            
            function removeActive(items) {
                for (let item of items) {
                    item.classList.remove('autocomplete-active');
                }
            }
            
            function closeAllLists() {
                const lists = document.getElementsByClassName('autocomplete-items');
                for (let list of lists) {
                    list.parentNode.removeChild(list);
                }
            }
            
            // Close autocomplete when clicking outside
            document.addEventListener('click', function(e) {
                if (e.target !== input) {
                    closeAllLists();
                }
            });
        });
    }

    /**
     * Dark Mode Toggle
     * Implements system-wide dark mode
     */
    initDarkMode() {
        // Check for saved preference or default to light mode
        const currentTheme = localStorage.getItem('theme') || 'light';
        document.documentElement.setAttribute('data-theme', currentTheme);
        
        // Create dark mode toggle button
        const darkModeToggle = document.createElement('button');
        darkModeToggle.className = 'dark-mode-toggle';
        darkModeToggle.setAttribute('aria-label', 'Toggle dark mode');
        darkModeToggle.innerHTML = currentTheme === 'dark' ? 
            '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>' :
            '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>';
        
        document.body.appendChild(darkModeToggle);
        
        darkModeToggle.addEventListener('click', () => {
            this.toggleDarkMode();
        });
        
        // Listen for system theme changes
        if (window.matchMedia) {
            window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
                if (!localStorage.getItem('theme')) {
                    const newTheme = e.matches ? 'dark' : 'light';
                    document.documentElement.setAttribute('data-theme', newTheme);
                    this.updateDarkModeToggle(newTheme);
                }
            });
        }
    }
    
    toggleDarkMode() {
        const currentTheme = document.documentElement.getAttribute('data-theme');
        const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
        
        document.documentElement.setAttribute('data-theme', newTheme);
        localStorage.setItem('theme', newTheme);
        this.updateDarkModeToggle(newTheme);
    }
    
    updateDarkModeToggle(theme) {
        const toggle = document.querySelector('.dark-mode-toggle');
        if (toggle) {
            toggle.innerHTML = theme === 'dark' ? 
                '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>' :
                '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>';
        }
    }

    /**
     * Performance Monitoring
     * Tracks and optimizes site performance
     */
    initPerformanceMonitoring() {
        if ('PerformanceObserver' in window) {
            // Monitor long tasks
            try {
                const longTaskObserver = new PerformanceObserver((list) => {
                    for (const entry of list.getEntries()) {
                        // Long Task detected - in production, send to analytics
                    }
                });
                longTaskObserver.observe({ entryTypes: ['longtask'] });
            } catch (e) {
                // Long task observer not supported
            }
            
            // Monitor paint timing
            try {
                const paintObserver = new PerformanceObserver((list) => {
                    for (const entry of list.getEntries()) {
                        // Track FCP and LCP - data available in entry object
                    }
                });
                paintObserver.observe({ entryTypes: ['paint', 'largest-contentful-paint'] });
            } catch (e) {
                // Paint observer not supported
            }
        }
        
        // Monitor page visibility changes
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                // Pause animations, videos, etc.
                this.pauseAnimations();
            } else {
                // Resume animations
                this.resumeAnimations();
            }
        });
        
        // Optimize images on slow connections
        if ('connection' in navigator) {
            const connection = navigator.connection;
            if (connection.effectiveType === '2g' || connection.saveData) {
                // Load lower quality images
                document.documentElement.classList.add('low-quality-images');
            }
        }
    }
    
    pauseAnimations() {
        // Pause all CSS animations
        document.querySelectorAll('*').forEach(el => {
            const computedStyle = window.getComputedStyle(el);
            if (computedStyle.animationPlayState === 'running') {
                el.style.animationPlayState = 'paused';
                el.dataset.wasAnimating = 'true';
            }
        });
    }
    
    resumeAnimations() {
        // Resume paused animations
        document.querySelectorAll('[data-was-animating="true"]').forEach(el => {
            el.style.animationPlayState = 'running';
            delete el.dataset.wasAnimating;
        });
    }
}

// Initialize all interactions
const pcrCardInteractions = new PCRCardInteractions();

// Export for use in other modules if needed
export default PCRCardInteractions;